import React from "react";
import { sessionIcsUrl } from "../lib/api";

/**
 * AddToCalendarButtons — three lightweight deep links for the three
 * calendars that account for ~95% of users we care about:
 *
 *   • Google Calendar — `calendar.google.com/calendar/render?action=TEMPLATE…`
 *   • Outlook (web)    — `outlook.live.com/calendar/0/deeplink/compose…`
 *   • Apple / .ics     — direct download of the RFC-5545 .ics from our backend
 *
 * Each link opens in a new tab. No OAuth, no SDK, no popup blockers —
 * just one-shot URL templates the calendar vendors publish.
 *
 * Props:
 *   sess: { session_id, title, game, starts_at, ends_at?, notes?, league_id?, timezone? }
 *   compact?: bool — render icon-only chips (used in dashboard rows)
 */
export function AddToCalendarButtons({ sess, compact = false }) {
  if (!sess?.starts_at) return null;
  const title = `${sess.title || "Session"} — ${sess.game || ""}`.trim().replace(/[—-]\s*$/, "").trim();
  const start = new Date(sess.starts_at);
  // Default 2h if no ends_at — mirrors backend build_ics_event behaviour.
  const end = sess.ends_at
    ? new Date(sess.ends_at)
    : new Date(start.getTime() + 2 * 3600 * 1000);
  const details = [
    sess.notes ? sess.notes : null,
    sess.timezone ? `Host timezone: ${sess.timezone}` : null,
    `View on LEAGUE HUB: https://leaguehub.co/sessions/${sess.session_id}`,
  ].filter(Boolean).join("\n");
  const location = "LEAGUE HUB";

  const googleHref =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${gcalDate(start)}/${gcalDate(end)}` +
    `&details=${encodeURIComponent(details)}` +
    `&location=${encodeURIComponent(location)}`;

  const outlookHref =
    "https://outlook.live.com/calendar/0/deeplink/compose" +
    `?subject=${encodeURIComponent(title)}` +
    `&startdt=${encodeURIComponent(start.toISOString())}` +
    `&enddt=${encodeURIComponent(end.toISOString())}` +
    `&body=${encodeURIComponent(details)}` +
    `&location=${encodeURIComponent(location)}` +
    "&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent";

  const icsHref = sessionIcsUrl(sess.session_id);

  const sz = compact
    ? "text-[9px] tracking-widest px-2 py-1"
    : "text-[10px] tracking-widest px-3 py-1.5";

  const items = [
    { key: "google",  label: "GOOGLE",  icon: "fab fa-google", href: googleHref, ext: true },
    { key: "outlook", label: "OUTLOOK", icon: "fab fa-microsoft", href: outlookHref, ext: true },
    { key: "apple",   label: ".ICS",    icon: "fab fa-apple",  href: icsHref, ext: false },
  ];

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`add-to-calendar-${sess.session_id}`}>
      {items.map((it) => (
        <a
          key={it.key}
          href={it.href}
          target={it.ext ? "_blank" : undefined}
          rel={it.ext ? "noopener noreferrer" : undefined}
          data-testid={`add-to-calendar-${it.key}-${sess.session_id}`}
          className={`${sz} inline-flex items-center gap-1 rounded-lg bg-white/5 text-white/80 border border-white/10 hover:border-[#14b8a6]/60 hover:text-[#14b8a6]`}
          title={`Add to ${it.label}`}
        >
          <i className={it.icon} />
          {!compact && <span>{it.label}</span>}
        </a>
      ))}
    </div>
  );
}

/** Format `2026-06-15T22:00:00Z` → `20260615T220000Z` (Google Calendar). */
function gcalDate(d) {
  const iso = d.toISOString();             // 2026-06-15T22:00:00.000Z
  return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

export default AddToCalendarButtons;
